import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import { mapTeamData } from "@components/team/TeamManagement/utils";
import React, {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
} from "react";

import { useAuth } from "./use-auth";
import { useLocalStorage } from "./use-local-storage";
import { useWatchUserWsEvent } from "./use-watch-user-ws-event";

const GET_USER_TEAMS_DETAILS = gql`
  query GetUserTeamsDetails {
    user {
      uuid
      teams {
        name
        uuid
        createdAt
        location
        organization {
          uuid
          name
          emailDomain
        }
        userTeams {
          roleId
          isAdmin
          isSelfSubscribed
          user {
            uuid
            email
            firstName
            lastName
            account {
              uuid
              accountType
            }
          }
        }
      }
    }
  }
`;

const EMPTY_TEAM = {
  name: "",
  users: [],
  notes: [],
  uuid: null,
  id: null,
};

// Initialize context with a default value
const TeamContext = createContext({
  currentTeam: EMPTY_TEAM,
  setCurrentTeam: () => {},
  refreshUserTeams: () => {},
  selectedTeamMembers: [],
  setSelectedTeamMembers: () => {},
});

export const TeamProvider = ({ children }) => {
  const { userUuid, isUserAuthenticated } = useAuth();
  const [currentTeam, setCurrentTeam] = useLocalStorage(
    "currentTeam",
    EMPTY_TEAM,
  );
  const [selectedTeamMembers, setSelectedTeamMembers] =
    useLocalStorage("selected-team-members", []);

  const [
    fetchTeamsDetails,
    { data: userTeamsDetails, refetch: refetchTeamsDetails, called },
  ] = useLazyQuery(GET_USER_TEAMS_DETAILS);

  const safeRefetchTeamsDetails = () => {
    if (called) {
      refetchTeamsDetails();
    } else {
      fetchTeamsDetails();
    }
  };

  const fetchOrRefetchTeamsDetails = useEffectEvent(
    safeRefetchTeamsDetails,
  );

  useEffect(() => {
    if (!userUuid || !isUserAuthenticated) return;
    fetchOrRefetchTeamsDetails();
  }, [userUuid, isUserAuthenticated]);

  useWatchUserWsEvent({
    onMessage: safeRefetchTeamsDetails,
    eventName: "user_update",
  });

  const userTeams =
    userTeamsDetails?.user?.teams?.map((team) =>
      mapTeamData({ team }),
    ) || null;

  // Ensure selected team is valid and up to date before exposing it
  const filteredCurrentTeam = (() => {
    const fetchedCurrentTeam = userTeams?.filter(
      (team) => team?.uuid === currentTeam?.uuid,
    )?.[0];
    return (
      fetchedCurrentTeam ||
      userTeams?.[0] || {
        name: "",
        users: [],
        notes: [],
        uuid: null,
      }
    );
  })();

  // Ensure selected member IDs are valid (i.e. part of the team) before exposing them
  const filteredSelectedTeamMembers = (() => {
    if (!filteredCurrentTeam) return [];

    const validIds = new Set(
      filteredCurrentTeam.users.map((user) => user.uuid),
    );
    const selectedIds = selectedTeamMembers.filter((uuid) =>
      validIds.has(uuid),
    );
    return selectedIds.length ? selectedIds : [userUuid];
  })();

  const teamByUuid = (teamUuid) => {
    if (!teamUuid || !userTeams) return null;
    return userTeams.find((t) => t.uuid === teamUuid) || null;
  };

  return (
    <TeamContext.Provider
      value={{
        userTeams,
        emptyTeam: EMPTY_TEAM,
        currentTeam: filteredCurrentTeam,
        setCurrentTeam,
        refreshUserTeams: safeRefetchTeamsDetails,
        selectedTeamMembers: filteredSelectedTeamMembers,
        setSelectedTeamMembers,
        teamByUuid,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  return useContext(TeamContext);
};
